import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Settings } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { designationService } from '../services/designationService';
import { fieldConfigService } from '../services/fieldConfigService';
import { useSelector } from 'react-redux';
import FieldManager from './FieldManager';

const DesignationSettingsModal = ({ isOpen, onClose, onDesignationsUpdate }) => {
  const { success, error } = useToast();
  const { user } = useSelector((state) => state.auth);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newDesignation, setNewDesignation] = useState({
    name: '',
    fieldType: 'non-teaching',
  });
  const [selectedDesignation, setSelectedDesignation] = useState(null);
  const [showFieldManager, setShowFieldManager] = useState(false);
  const [tempDesignation, setTempDesignation] = useState(null); // Temporary designation before save

  useEffect(() => {
    if (isOpen) {
      loadDesignations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadDesignations = async () => {
    setLoading(true);
    try {
      const response = await designationService.getAllDesignations(user.token);
      // Handle response - might be array directly or wrapped in response object
      const data = Array.isArray(response) ? response : (response.data || response);
      console.log('Designations loaded:', data);
      setDesignations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading designations:', err);
      error('Failed to load designations');
      setDesignations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newDesignation.name.trim()) {
      error('Please enter a designation name');
      return;
    }

    // Create temporary designation object (not saved to DB yet)
    const tempDes = {
      _id: 'temp_' + Date.now(), // Temporary ID
      name: newDesignation.name.trim(),
      fieldType: newDesignation.fieldType,
      isDefault: false,
      isTemporary: true, // Flag to identify unsaved designation
    };

    setTempDesignation(tempDes);
    setNewDesignation({ name: '', fieldType: 'basic' });
    setSelectedDesignation(tempDes);
    setShowFieldManager(true);
  };

  // Save designation and fields to database
  const handleSaveDesignationWithFields = async (fields) => {
    if (!tempDesignation) return;

    try {
      // First, save the designation
      const addedDesignation = await designationService.addDesignation(
        tempDesignation.name,
        tempDesignation.fieldType,
        user.token
      );
      
      const designation = addedDesignation.data || addedDesignation;

      // Then, save all configured fields
      if (fields && fields.length > 0) {
        for (const field of fields) {
          const { _id, ...fieldData } = field;
          await fieldConfigService.addField(designation._id, fieldData, user.token);
        }
      }

      // Reload designations from database to get fresh data
      await loadDesignations();
      success('Designation and fields saved successfully');
      onDesignationsUpdate();
      
      // Clear temporary state
      setTempDesignation(null);
      setShowFieldManager(false);
      setSelectedDesignation(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to save designation';
      error(errorMessage);
    }
  };

  // Cancel designation creation
  const handleCancelDesignation = () => {
    if (tempDesignation && window.confirm('Discard this designation and all configured fields?')) {
      setTempDesignation(null);
      setShowFieldManager(false);
      setSelectedDesignation(null);
      setNewDesignation({ name: '', fieldType: 'non-teaching' });
    } else if (!tempDesignation) {
      setShowFieldManager(false);
      setSelectedDesignation(null);
    }
  };

  const handleDelete = async (id) => {
    // Removed isDefault check to allow all designations to be deletable

    if (!window.confirm('Are you sure you want to delete this designation?')) {
      return;
    }

    try {
      await designationService.deleteDesignation(id, user.token);
      setDesignations(designations.filter(d => d._id !== id));
      success('Designation deleted successfully');
      onDesignationsUpdate();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to delete designation';
      error(errorMessage);
    }
  };

  const getFieldTypeLabel = (fieldType) => {
    switch (fieldType) {
      case 'teaching':
        return '📚 Teaching Staff';
      case 'non-teaching':
        return '👔 Non-Teaching Staff';
      default:
        return fieldType;
    }
  };

  const getFieldTypeBadgeClass = (fieldType) => {
    switch (fieldType) {
      case 'teaching':
        return 'bg-blue-50 text-[#1B6CA8] border border-blue-100/50';
      case 'non-teaching':
        return 'bg-slate-50 text-slate-600 border border-slate-200/50';
      default:
        return 'bg-slate-50 text-slate-500 border border-slate-200/50';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-white rounded-[2rem] shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col premium-shadow">
        {/* Modern Header */}
        <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between primary-gradient-bg">
          <div className="flex items-center space-x-4">
            <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-md">
              <Settings className="text-white" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white leading-tight">Staff Roles</h2>
              <p className="text-blue-100 text-xs font-medium tracking-wide uppercase mt-0.5">Manage Designations</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all duration-300"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8 space-y-8">
          {/* Enhanced Add Section */}
          <div className="p-6 bg-slate-50 rounded-[1.5rem] border border-slate-100/50">
            <div className="flex items-center gap-2 mb-4">
              <Plus size={18} className="text-[#1B6CA8]" />
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">Create New Designation</h3>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 group">
                <input
                  type="text"
                  value={newDesignation.name}
                  onChange={(e) => setNewDesignation({ ...newDesignation, name: e.target.value })}
                  placeholder="e.g. Senior Faculty"
                  className="w-full px-4 py-3.5 bg-white border border-slate-200 rounded-2xl focus:ring-4 focus:ring-[#1B6CA8]/10 focus:border-[#1B6CA8] outline-none transition-all font-medium text-slate-700"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') handleAdd();
                  }}
                />
              </div>
              <select
                value={newDesignation.fieldType}
                onChange={(e) => setNewDesignation({ ...newDesignation, fieldType: e.target.value })}
                className="px-4 py-3.5 bg-white border border-slate-200 rounded-2xl focus:ring-4 focus:ring-[#1B6CA8]/10 focus:border-[#1B6CA8] outline-none font-bold text-slate-600 cursor-pointer min-w-[160px]"
              >
                <option value="teaching">📚 Teaching</option>
                <option value="non-teaching">👔 Non-Teaching</option>
              </select>
              <button
                onClick={handleAdd}
                className="px-6 py-3.5 primary-gradient-bg text-white rounded-2xl hover:shadow-lg hover:shadow-blue-200/50 transition-all duration-300 font-bold flex items-center justify-center gap-2 active:scale-95"
              >
                <Plus size={20} />
                <span>Add Role</span>
              </button>
            </div>
          </div>

          {/* Designation Cards Grid */}
          <div>
            <div className="flex items-center justify-between mb-4 px-2">
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">Current Roles</h3>
              <span className="text-[10px] font-black bg-slate-100 text-slate-500 px-2.5 py-1 rounded-full">{designations.length} TOTAL</span>
            </div>
            
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12 gap-4">
                <div className="w-10 h-10 border-4 border-slate-100 border-t-[#1B6CA8] rounded-full animate-spin"></div>
                <p className="text-sm font-bold text-slate-400 italic">Syncing roles...</p>
              </div>
            ) : designations.length === 0 ? (
              <div className="text-center py-12 bg-slate-50 rounded-[1.5rem] border border-dashed border-slate-200">
                <Settings size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500 font-bold">No designations established yet</p>
                <p className="text-slate-400 text-xs mt-1">Start by adding your first staff role above</p>
              </div>
            ) : (
              <div className="space-y-3">
                {designations.map((designation) => (
                  <div
                    key={designation._id}
                    className="group flex flex-col sm:flex-row sm:items-center justify-between p-5 bg-white border border-slate-100 rounded-[1.5rem] hover:border-blue-200 hover:shadow-xl hover:shadow-slate-200/40 transition-all duration-300 gap-4"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-slate-800 text-lg group-hover:text-[#1B6CA8] transition-colors">{designation.name}</span>
                        {designation.isDefault && (
                          <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 text-[10px] font-black uppercase tracking-tighter rounded-lg border border-emerald-100">
                            System Default
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded-lg ${getFieldTypeBadgeClass(designation.fieldType)}`}>
                          {getFieldTypeLabel(designation.fieldType)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => {
                          setSelectedDesignation(designation);
                          setShowFieldManager(true);
                        }}
                        className="flex-1 sm:flex-none px-5 py-2.5 text-xs font-bold bg-[#1B6CA8]/10 text-[#1B6CA8] rounded-xl hover:bg-[#1B6CA8] hover:text-white transition-all duration-300"
                      >
                        Design Form Fields
                      </button>
                      <button
                        onClick={() => handleDelete(designation._id)}
                        className="p-2.5 rounded-xl text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all duration-300"
                        title="Remove role"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Action Footer */}
        <div className="px-8 py-6 bg-slate-50 border-t border-slate-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-8 py-3 bg-white text-slate-600 font-bold rounded-2xl border border-slate-200 hover:bg-slate-100 hover:border-slate-300 transition-all duration-300 active:scale-95"
          >
            Finished
          </button>
        </div>
      </div>


      {/* Field Manager Modal */}
      {showFieldManager && selectedDesignation && (
        <FieldManager
          designation={selectedDesignation}
          isOpen={showFieldManager}
          onClose={handleCancelDesignation}
          onSave={tempDesignation ? handleSaveDesignationWithFields : undefined}
          isTemporary={!!tempDesignation}
        />
      )}
    </div>
  );
};

export default DesignationSettingsModal;
